import { useState, useEffect } from 'react'
import AuthService from 'services/auth'
import cutileiApi from 'services/cutileiApi'
import { getApiErrorMessage, logApiError } from 'services/apiError'
import { brandLabel, UNREGISTERED_MACHINE } from 'services/CardMachine'
import SecondaryButton from 'components/Buttons/SecondaryButton'
import { ReactComponent as Loading } from 'icons/loading2.svg'
import { Container, Row } from '../styles'
import CardMachineForm from './CardMachineForm'
import {
  List, HintText, WarningText, MachineRow, MachineItem, MachineName, DefaultBadge, DefaultButton
} from './styles'

const NEW_MACHINE = 'new'
const UNREGISTERED = 'unregistered'

const brandNames = machine => (machine.brands ?? []).map(brandLabel).join(', ')

export default function ConfigCardMachines() {
  const businessId = AuthService.getBusinessId()
  const canEdit = AuthService.hasPermission('business_config.card_machines', 'edit')

  const [loading, setLoading] = useState(true)
  const [machines, setMachines] = useState([])
  // null = lista, NEW_MACHINE = cadastro, UNREGISTERED = tabela zerada (só leitura), id = edição.
  const [selected, setSelected] = useState(null)
  const [errorMessage, setErrorMessage] = useState(null)
  const [savingDefault, setSavingDefault] = useState(false)

  useEffect(() => {
    getData()
  }, [])

  const getData = async () => {
    try {
      setLoading(true)
      const { data } = await cutileiApi.get(
        `/businesses/${businessId}/card_machines`, AuthService.getRequestAuth()
      )

      setMachines(data)
    } catch (error) {
      logApiError(error)
      setErrorMessage(getApiErrorMessage(error))
    } finally {
      setLoading(false)
    }
  }

  const backToList = () => setSelected(null)

  const handleSaved = saved => {
    setMachines(selected === NEW_MACHINE
      ? [...machines, saved]
      : machines.map(item => item.id === saved.id ? saved : item))
    backToList()
  }

  const handleDeleted = deleted => {
    setMachines(machines.filter(item => item.id !== deleted.id))
    backToList()
  }

  // Só uma maquininha é a padrão: a API tira a marca da anterior, e a lista acompanha.
  const toggleDefault = async machine => {
    const isDefault = !machine.is_default

    try {
      setSavingDefault(true)
      setErrorMessage(null)

      await cutileiApi.put(
        `/businesses/${businessId}/card_machines/${machine.id}/default`, { isDefault }, AuthService.getRequestAuth()
      )

      setMachines(machines.map(item => ({ ...item, is_default: isDefault && item.id === machine.id })))
    } catch (error) {
      logApiError(error)
      setErrorMessage(getApiErrorMessage(error))
    } finally {
      setSavingDefault(false)
    }
  }

  const selectedMachine = machines.find(machine => machine.id === selected)

  if (loading) return <Container><Loading/></Container>

  if (selected === UNREGISTERED) {
    return (
      <Container>
        <Row>
          <SecondaryButton label='Voltar' onClick={backToList} width={120}/>
        </Row>
        <List>
          <CardMachineForm key={UNREGISTERED} machine={UNREGISTERED_MACHINE} canEdit={false}/>
        </List>
      </Container>
    )
  }

  if (selected === NEW_MACHINE || selectedMachine) {
    return (
      <Container>
        <List>
          <CardMachineForm
            key={selected}
            machine={selectedMachine ?? null}
            canEdit={canEdit}
            onSaved={handleSaved}
            onDeleted={handleDeleted}
            onCancel={backToList}
          />
        </List>
      </Container>
    )
  }

  return (
    <Container>
      {canEdit && (
        <Row>
          <SecondaryButton label='Nova maquininha' onClick={() => setSelected(NEW_MACHINE)} width={180}/>
        </Row>
      )}
      {errorMessage && <WarningText>{errorMessage}</WarningText>}
      <List>
        <MachineRow>
          <MachineItem type='button' onClick={() => setSelected(UNREGISTERED)}>
            <MachineName>{UNREGISTERED_MACHINE.name}</MachineName>
            <HintText>Padrão do sistema · taxas zeradas · {brandNames(UNREGISTERED_MACHINE)}</HintText>
          </MachineItem>
        </MachineRow>
        {machines.map(machine => (
          <MachineRow key={machine.id}>
            <MachineItem type='button' onClick={() => setSelected(machine.id)}>
              <MachineName>
                {machine.name}
                {machine.is_default && <DefaultBadge>Padrão</DefaultBadge>}
              </MachineName>
              <HintText>{brandNames(machine)}</HintText>
            </MachineItem>
            {canEdit && (
              <DefaultButton type='button' disabled={savingDefault} onClick={() => toggleDefault(machine)}>
                {machine.is_default ? 'Remover padrão' : 'Tornar padrão'}
              </DefaultButton>
            )}
          </MachineRow>
        ))}
        {machines.length === 0 && !errorMessage && (
          <HintText>Nenhuma maquininha cadastrada.</HintText>
        )}
      </List>
    </Container>
  )
}
